import type { Breakpoint } from '@mui/material/styles';
import type { UseInViewOptions } from 'framer-motion';
import { useInView } from 'framer-motion';
import { mergeClasses, mergeRefs } from 'minimal-shared/utils';
import { forwardRef, startTransition, useCallback, useEffect, useRef, useState } from 'react';
import imageLoader, { availableImageSizes } from '../../utils/tools/imageLoader';
import { imageClasses } from './classes';
import { ImageImg, ImageOverlay, ImagePlaceholder, ImageRoot } from './styles';
import type { EffectsType } from './styles';

// ----------------------------------------------------------------------

type AspectRatioType = '1/1' | '4/3';

export type ImageProps = React.ComponentProps<typeof ImageRoot> & {
    src?: string;
    alt?: string;
    delayTime?: number;
    externalImage?: boolean;
    onLoad?: () => void;
    effect?: EffectsType;
    visibleByDefault?: boolean;
    disablePlaceholder?: boolean;
    viewportOptions?: UseInViewOptions;
    ratio?: AspectRatioType | Partial<Record<Breakpoint, AspectRatioType>>;
    slotProps?: {
        img?: Omit<React.ComponentProps<typeof ImageImg>, 'src' | 'alt'>;
        overlay?: React.ComponentProps<typeof ImageOverlay>;
        placeholder?: React.ComponentProps<typeof ImagePlaceholder>;
    };
};

const DEFAULT_DELAY = 0;
const DEFAULT_EFFECT: EffectsType = {
    style: 'blur',
    duration: 300,
    disabled: false,
};

const Image = forwardRef<HTMLSpanElement, ImageProps>(function Image(props, ref) {
    const { sx, src, ratio, onLoad, effect, alt = '', externalImage, slotProps, className, viewportOptions, disablePlaceholder, visibleByDefault = false, delayTime = DEFAULT_DELAY, ...other } = props;

    const localRef = useRef<HTMLSpanElement | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [widthToUse, setWidthToUse] = useState<string>('100px');

    useEffect(() => {
        setWidthToUse(`${localRef?.current?.getBoundingClientRect()?.width}px`);
    }, [localRef]);

    const isInView = useInView(localRef, {
        once: true,
        ...viewportOptions,
    });

    const handleImageLoad = useCallback(() => {
        const timer = setTimeout(() => {
            startTransition(() => {
                setIsLoaded(true);
                onLoad?.();
            });
        }, delayTime);

        return () => clearTimeout(timer);
    }, [delayTime, onLoad]);

    const finalEffect = {
        ...DEFAULT_EFFECT,
        ...effect,
    };

    const shouldRenderImage = visibleByDefault || isInView;
    const showPlaceholder = !visibleByDefault && !isLoaded && !disablePlaceholder;

    const renderComponents = {
        overlay: () => slotProps?.overlay && <ImageOverlay className={imageClasses.overlay} {...slotProps.overlay} />,
        placeholder: () => showPlaceholder && <ImagePlaceholder className={imageClasses.placeholder} {...slotProps?.placeholder} />,
        image: () => (
            <ImageImg
                alt={alt}
                onLoad={handleImageLoad}
                className={imageClasses.img}
                {...(externalImage
                    ? {
                          alt: alt,
                          src: src,
                      }
                    : {
                          alt: alt,
                          sizes: widthToUse,
                          src: imageLoader({ src }),
                          srcSet: availableImageSizes?.map((width) => imageLoader({ src, width }) + ' ' + width + 'w').join(', '),
                      })}
                {...slotProps?.img}
            />
        ),
    };

    return (
        <ImageRoot
            ref={mergeRefs([localRef, ref])}
            effect={visibleByDefault || finalEffect.disabled ? undefined : finalEffect}
            className={mergeClasses([imageClasses.root, className], {
                [imageClasses.state.loaded]: !visibleByDefault && isLoaded,
            })}
            sx={[
                {
                    '--aspect-ratio': ratio,
                    ...(!!ratio && { width: 1 }),
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...other}
        >
            {renderComponents.overlay()}
            {renderComponents.placeholder()}
            {shouldRenderImage && renderComponents.image()}
        </ImageRoot>
    );
});

export default Image;
